/**
 * ⭐ 审批操作弹窗：同意（意见可选）/ 退回（意见必填）。
 *
 * 后端会校验「是否轮到你审批」，错误码 `APPROVAL_NOT_YOUR_TURN` 在此友好展示。
 */

import { useEffect, useState } from 'react';
import Button from '../../components/Button';
import { ApiError } from '../../api/client';
import { useApprove, useReject, useTerminateApproval } from './useApprovals';
import type { ApprovalInstanceInfo } from '@atlaspm/shared';

export type ApprovalAction = 'approve' | 'reject' | 'terminate';

export interface ApproveDialogProps {
  item: ApprovalInstanceInfo | null;
  action: ApprovalAction;
  open: boolean;
  onClose(): void;
  onDone?(): void;
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'APPROVAL_SELF_NOT_ALLOWED')
      return '你是该任务的提交人，不能审批自己提交的任务，需由他人复核';
    if (err.code === 'APPROVAL_NODE_ALREADY_APPROVED_BY_YOU')
      return '你已审批过本流程的前置节点，同一人不能连续审批多个节点，需由他人复核';
    if (err.code === 'APPROVAL_NOT_YOUR_TURN') return '当前节点未轮到你审批，请刷新列表';
    if (err.code === 'APPROVAL_ALREADY_DONE') return '该审批已办结';
    if (err.code === 'APPROVAL_TERMINATE_NOT_EXPIRED') return '该审批仍在进行中，不能终止（只能终止已超时的审批）';
    if (err.code === 'APPROVAL_TERMINATE_STATE_INVALID') return '任务已不在审批中，无需终止';
    if (err.code === 'APPROVAL_TERMINATE_FORBIDDEN') return '只有提交人本人或有审批/任务管理权限的人可以终止';
    return err.message;
  }
  return err instanceof Error ? err.message : '操作失败';
}

export default function ApproveDialog({
  item,
  action,
  open,
  onClose,
  onDone,
}: ApproveDialogProps) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approve = useApprove();
  const reject = useReject();
  const terminate = useTerminateApproval();

  const isReject = action === 'reject';
  const isTerminate = action === 'terminate';
  const mutation = isTerminate ? terminate : isReject ? reject : approve;
  const busy = mutation.isPending;

  useEffect(() => {
    if (open) {
      setComment('');
      setError(null);
      approve.reset();
      reject.reset();
      terminate.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, item?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  const submit = () => {
    setError(null);
    if (isReject && comment.trim() === '') {
      setError('退回必须填写意见');
      return;
    }
    if (isTerminate && comment.trim().length < 4) {
      setError('终止原因至少 4 字');
      return;
    }
    const onError = (err: unknown) => setError(friendlyError(err));
    const done = { onSuccess: () => { onDone?.(); onClose(); }, onError };
    if (isTerminate) {
      terminate.mutate({ taskId: item.taskId, reason: comment.trim() }, done);
    } else if (isReject) {
      reject.mutate({ id: item.id, comment: comment.trim() }, done);
    } else {
      approve.mutate({ id: item.id, comment: comment.trim() || undefined }, done);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-box--sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={['modal-box__title', isReject || isTerminate ? 'is-danger' : ''].join(' ')}>
          {isTerminate ? '终止超时审批' : isReject ? '退回审批' : '同意审批'} ·{' '}
          {item.taskName ?? `任务 #${item.taskId}`}
        </div>

        <div className="modal-box__body">
          <label className="modal-field">
            <span className="modal-field__label">
              {isTerminate
                ? '终止原因（必填，至少 4 字）'
                : isReject
                  ? '退回意见（必填）'
                  : '审批意见（可选）'}
            </span>
            <textarea
              rows={4}
              value={comment}
              placeholder={
                isTerminate
                  ? '请说明为何终止该超时审批（如：审批人离岗、业务取消、重新发起等）'
                  : isReject
                    ? '请说明退回原因，便于提交人修改'
                    : '可填写审批意见'
              }
              onChange={(e) => setComment(e.target.value)}
            />
          </label>

          {error && <div className="cf-error">⚠ {error}</div>}

          <div className="modal-box__foot">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              取消
            </Button>
            <Button
              variant={isReject || isTerminate ? 'danger' : 'primary'}
              onClick={submit}
              disabled={busy}
            >
              {busy ? '提交中…' : isTerminate ? '确认终止' : isReject ? '确认退回' : '确认同意'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
